<template>
    <div class="flex flex-col w-full activity-guide-config text-default-text">
        <RedesignFormItem
            :label="$t('queue.activity.to.display')"
            class="!mb-1"
        >
            <RedesignBaseSelect
                :value="localModel.ShowActivities"
                :data="activitiesToDisplay"
                :config="{ labelKey: 'label', valueKey: 'value' }"
                @change="updateShowActivities"
            />
        </RedesignFormItem>

        <div
            v-if="displayMainCountSelector"
            class="w-full"
        >
            <RedesignFormItem
                :label="$t('widget.displayAsMainCount')"
                :info-text="String($t('widget.selectCountBiggerCircle'))"
                class="!mb-1"
            >
                <RedesignRadioGroup
                    :value="localModel.mainActivity"
                    class="flex-col gap-2"
                    @input="updateMainActivity"
                >
                    <RedesignRadio
                        v-for="option in activitiesToDisplay"
                        :key="option.key"
                        :label="option.key"
                    >
                        {{ $t(option.value) }}
                    </RedesignRadio>
                </RedesignRadioGroup>
            </RedesignFormItem>
        </div>
        <div
            v-if="localModel.AnswerCount"
            class="pt-3"
        >
            <div class="pb-3">
                {{ $t('answer.activity.styles') }}
            </div>

            <div class="flex flex-row">
                <div class="flex-col">
                    <span class="text-main-sm">{{ $t('settings.color') }}</span>
                    <BaseColorPicker
                        :value="localModel.AnswerCount.color"
                        :predefine="predefinedColors"
                        size="small"
                        @change="v => updateAnswerStyle('color', v)"
                    />
                </div>

                <div class="w-full px-4">
                    <span class="text-main-sm">{{ $t('settings.fonSize') }}</span>
                    <RedesignInputNumber
                        :value="localModel.AnswerCount.fontSize"
                        :min-value="min"
                        :max-value="max"
                        :step="1"
                        size="small"
                        @update:value="v => updateAnswerStyle('fontSize', v)"
                    />
                </div>
            </div>
        </div>
        <div
            v-if="localModel.InSLACount"
            class="pt-3"
        >
            <div class="pb-3">
                {{ $t('inSLA.activity.styles') }}
            </div>

            <div class="flex flex-row">
                <div class="flex-col">
                    <span class="text-main-sm">{{ $t('settings.color') }}</span>
                    <BaseColorPicker
                        :value="localModel.InSLACount.color"
                        :predefine="predefinedColors"
                        size="small"
                        @change="v => updateInSLAStyle('color', v)"
                    />
                </div>

                <div class="w-full px-4">
                    <span class="text-main-sm">{{ $t('settings.fonSize') }}</span>
                    <RedesignInputNumber
                        :value="localModel.InSLACount.fontSize"
                        :min-value="min"
                        :max-value="max"
                        :step="1"
                        @update:value="(v) => updateInSLAStyle('fontSize', v)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>


<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { WidgetLayout } from '@/types/dashboard'
import {
    activitiesToDisplay,
    BASE_ACTIVITY_FONT,
    activitiesToDisplayEnum
} from '@/enum/redesignQueueDashboardStatistics'
import colors from '@/enum/colors'
import { activeLayoutColors } from '@/composables/globalData/useLayouts'
import uniq from 'lodash/uniq'
import values from 'lodash/values'

import RedesignFormItem from '@/components/RedesignBaseComponents/Form/RedesignFormItem.vue'
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import RedesignRadioGroup from '@/components/RedesignBaseComponents/Inputs/RedesignRadioGroup.vue'
import RedesignRadio from '@/components/RedesignBaseComponents/Inputs/RedesignRadio.vue'
import RedesignInputNumber from '@/components/RedesignBaseComponents/Inputs/RedesignInputNumber.vue'
import BaseColorPicker from '@/components/RedesignBaseComponents/Form/BaseColorPicker.vue'

const min = 4
const max = 32

const DEFAULT_ACTIVITY_STYLE = {
    AnswerCount: {
        color: colors.QUEUE_LIGHT_GREEN,
        fontSize: BASE_ACTIVITY_FONT
    },
    InSLACount: {
        color: colors.QUEUE_LUNCH_COLOR,
        fontSize: BASE_ACTIVITY_FONT
    }
}

const props = defineProps<{ value: WidgetLayout }>()

const emit = defineEmits<{
    (e: 'update:value', v: WidgetLayout): void
    (e: 'input', v: WidgetLayout): void
}>()


const localModel = ref<WidgetLayout>(
    normalizeActivityLayout(props.value)
)
//Computed
const displayMainCountSelector = computed(() => {
    return localModel.value.ShowActivities?.length === activitiesToDisplay.length
})

const predefinedColors = computed(() => {
    return uniq(values(activeLayoutColors.value))
})

//Methods
function normalizeActivityLayout (layout?: WidgetLayout): WidgetLayout {
    const merged: WidgetLayout = { ...(layout ?? {}) }

    if (!Array.isArray(merged.ShowActivities)) {
        merged.ShowActivities = [activitiesToDisplayEnum.answerCount, activitiesToDisplayEnum.InSLACount]
    }

    if (!merged.mainActivity) {
        merged.mainActivity = activitiesToDisplayEnum.answerCount
    }

    merged.AnswerCount = {
        ...DEFAULT_ACTIVITY_STYLE.AnswerCount,
        ...(merged.AnswerCount ?? {})
    }

    merged.InSLACount = {
        ...DEFAULT_ACTIVITY_STYLE.InSLACount,
        ...(merged.InSLACount ?? {})
    }

    return merged
}

function commit (next: WidgetLayout) {
    localModel.value = next
    emit('input', next)
    emit('update:value', next)
}

function updateShowActivities (value: string[]) {
    commit({
        ...localModel.value,
        ShowActivities: value
    })
}

function updateMainActivity (value: string) {
    commit({
        ...localModel.value,
        mainActivity: value
    })
}

function updateAnswerStyle (
    field: 'color' | 'fontSize',
    value: string | number
) {
    commit({
        ...localModel.value,
        AnswerCount: {
            ...localModel.value.AnswerCount,
            [field]: value
        }
    })
}

function updateInSLAStyle (
    field: 'color' | 'fontSize',
    value: string | number
) {
    commit({
        ...localModel.value,
        InSLACount: {
            ...localModel.value.InSLACount,
            [field]: value
        }
    })
}

watch(
    () => props.value,
    (val) => {
        localModel.value = normalizeActivityLayout(val)
    }
)
</script>
